import { Currency, Country } from '@ui/src';
import { Account } from '../accounts/types';

export enum CurrencyType {
  ALL = 'ALL',
  NONE = 'NONE',
  SPECIFIC = 'SPECIFIC',
}

export enum ConnectionTypeEnum {
  CSM = 'CSM',
  VIRTUAL_IBAN = 'VIRTUAL_IBAN',
  NO_IBAN = 'NO_IBAN',
}

export enum PspPartnerTypeEnum {
  CREDIT = 'CREDIT',
  PAYMENT = 'PAYMENT',
  EMONEY = 'EMONEY',
}

export enum PspPartnerLocationEnum {
  DOMESTIC = 'DOMESTIC',
  FOREIGN = 'FOREIGN',
}

export enum ExportFileTypeEnum {
  HARBOUR_HILLS_B2B_LITE = 'HARBOUR_HILLS_B2B_LITE',
  SPITAMEN = 'SPITAMEN',
}

export interface ConnectionType {
  pspIntegrationPaymentId: string;
  connectionType: ConnectionTypeEnum;
  id: string;
}

export interface PspIntergrationCurrency {
  pspIntegrationPaymentId: string;
  currencyId: string;
  currency: Currency;
  id: string;
}

export interface PspPartner {
  partnerType: PspPartnerTypeEnum;
  location: PspPartnerLocationEnum;
  paymentsTypes: string[];
  companyName: string;
  legalName: string;
  swift?: string;
  sortCode?: string;
  pspIntegrationId?: string;
  pspIntegration?: {
    name: string;
    pspIntegrationPayments: PspIntegrationPayment[];
    id: string;
    externalCustomersSupported?: boolean;
  };
  caspId?: string;
  address: string;
  managerContacts?: string;
  onlineBankLink?: string;
  description?: string;
  active: boolean;
  visibleToClients: boolean;
  countryId: string;
  country: Country;
  intermediaryBankName?: string;
  intermediaryBankSwift?: string;
  accountOfCorrespondentWithIntermediaryBank?: string;
  beneficiary?: string;
  exportFileType?: ExportFileTypeEnum;
  id: string;
}

export interface PaymentsPartnersSetting {
  pspIntegrationPaymentId: string;
  pspIntegrationPayment: PspIntegrationPayment;
  pspPartnerId: string;
  pspPartner: PspPartner;
  bic?: string;
  sortCode?: string;
  id: string;
}

export interface PspIntegrationPayment {
  pspIntegrationId: string;
  paymentsTypes: string[];
  currencyType: CurrencyType;
  connectionsTypes: ConnectionType[];
  currencies: PspIntergrationCurrency[];
  paymentsPartnersSettings: PaymentsPartnersSetting[];
  id: string;
}

export interface ExternalAccount {
  account: Account & { organization: { type: string } };
  accountId: string;
  iban: string;
  id: string;
  pspIntegrationConnectionId: string;
  pspIntegrationPayment?: PspIntegrationPayment;
  pspIntegrationPaymentId?: string;
  pspPartner?: PspPartner;
  bank?: string;
  currencyId?: string;
  paymentType?: string;
  accountNumber?: string;
  accountName?: string;
  accountType?: string;
  active?: boolean;
  createdAt?: string;
  createType?: string;
  caspId?: string;
  render?: Record<string, React.JSX.Element>;
  currenciesIds?: string[];
  pspIntegrationPaymentAccountsCount?: number;
}

export interface ExternalAccountFormValues {
  pspPartnerId: string;
  accountId: string;
  iban: string;
  paymentType: string;
  currencyId: string[];
  status?: string;
  active: boolean;
  caspId?: string;
  currenciesIds: string[];
}

export interface ExternalAccountWithIntegrationFormValues {
  accountsIds: string[];
  integrationId: string;
  currenciesIds: string[];
  caspId?: string;
}

export interface PspIntegration {
  id: string;
  name: string;
  externalCustomersSupported?: boolean;
  pspIntegrationPayments: {
    id: string;
    currencies: {
      id: string;
      currency: {
        id: string;
        name: string;
      };
    }[];
  }[];
}

export interface ExternalAccountTopUpOption {
  accountNumber: string;
  bankAccountHolderName: string;
  bankAddress: string;
  bankCountry: string;
  bankName: string;
  bic: string;
  createdAt: string;
  currencyId: string;
  iban: string;
  id: string;
  paymentType?: string;
  pspIntegrationAccountId: string;
  routingCodeEntries?: {
    routingCodeKey: string;
    routingCodeValue: string;
  }[];
  thirdPartyIntegrationId?: string;
  thirdPartyReference?: string;
}

export enum RoutingCodeKeyEnum {
  ABA = 'ABA',
  BLZ_CODE = 'BLZ_CODE',
  BRANCH_CODE = 'BRANCH_CODE',
  BSB_CODE = 'BSB_CODE',
  SORT_CODE = 'SORT_CODE',
  IFS_CODE = 'IFS_CODE',
  CLABE_CODE = 'CLABE_CODE',
  CTN = 'CTN',
}
export interface ExternalAccountTopUpOptionFormValues {
  id?: string;
  paymentType: string;
  currencyId: string;
  bankAccountHolderName: string;
  accountNumber: string;
  bankAddress: string;
  bic: string;
  iban: string;
  bankName: string;
  thirdPartyReference?: string;
  pspIntegrationAccountId?: string;
  routingCodeEntries?:
    | {
        id?: string;
        routingCodeKey: string;
        routingCodeValue: string;
      }[]
    | null;
}

export interface LinkedClient {
  active: boolean;
  createdAt: string;
  externalId: string;
  id: string;
  organizationId: string;
  organization: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    id: string;
  };
  organizationType: string;
  pspPartnerId?: string;
  render?: Record<string, React.JSX.Element>;
}

export interface LinkedClientsFormValues {
  organizationId: string;
  organizationType: string;
  externalId: string;
  active: boolean;
  pspPartnerId: string;
}
export interface PspIntegrationOrganization {
  externalId: string;
  name: string;
  organizationType: string;
}

export interface Organization {
  id: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  type: string;
}
